import { useNavigate } from "react-router-dom";
import { useComfort } from "../ComfortContext";
import { fill, getDictionary, type Locale } from "../../i18n/dictionary";
import { weekSignals } from "../../progress/roster";
import { localeTag } from "../../progress/text";
import { useOutletContext } from "react-router-dom";
import { useLinkedChildren } from "./useLinkedChildren";
import { LoadingChildren, ChildrenLoadError } from "./LoadState";
import type { LinkedChild } from "./data";
import { ChildRow } from "./ChildRow";
import { SyncNote } from "./SyncNote";

function formatDate(locale: Locale, iso: string): string {
  return new Date(iso).toLocaleDateString(localeTag(locale), { day: "numeric", month: "short" });
}

function thisWeekStats(linked: LinkedChild[]): {
  sessions: number;
  activeChildren: number;
  summariesShared: number;
} {
  const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
  let sessions = 0;
  const activeSet = new Set<string>();
  for (const lc of linked) {
    if (lc.linkState !== "active") continue;
    for (const s of lc.sessions) {
      if (s.at >= weekAgo) {
        sessions++;
        activeSet.add(lc.child.id);
      }
    }
  }
  const summariesShared = linked.filter(
    (lc) => lc.linkState === "active" && lc.link.scope.includes("progress"),
  ).length;
  return { sessions, activeChildren: activeSet.size, summariesShared };
}

// ---- Stat tile ---------------------------------------------------------------

function StatTile({ value, label }: { value: string; label: string }) {
  return (
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid var(--line)",
        borderRadius: "var(--r-lg)",
        padding: "16px 18px",
      }}
    >
      <span
        style={{
          display: "block",
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: 30,
          color: "var(--ink-deep)",
          lineHeight: 1,
        }}
      >
        {value}
      </span>
      <span
        style={{
          display: "block",
          fontSize: 13.5,
          color: "var(--ink-soft)",
          marginTop: 6,
        }}
      >
        {label}
      </span>
    </div>
  );
}

// ---- Section heading ---------------------------------------------------------

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2
      style={{
        fontFamily: "var(--font-display)",
        fontWeight: 700,
        fontSize: 13,
        letterSpacing: ".12em",
        textTransform: "uppercase",
        color: "var(--ink-faint)",
        margin: "0 0 12px",
      }}
    >
      {children}
    </h2>
  );
}

// ---- This week's signals for one child (design §7c) ----------------------------
//
// Neutral counts only. This replaced a "going well / worth exploring" label
// derived from a 0.7 accuracy threshold — an assessment drawn in colour.

function SignalLines({
  lc,
  dict,
}: {
  lc: LinkedChild;
  dict: ReturnType<typeof getDictionary>;
}) {
  const t = dict.service.home;
  const sig = weekSignals(lc);
  const quiet = sig.daysPlayed === 0 && sig.stepsMovedUp === 0 && sig.suggestionsSeen === 0;
  if (quiet) return <span style={{ fontSize: 13.5, color: "var(--ink-soft)" }}>{t.rosterQuiet}</span>;
  const parts = [fill(t.rosterDays, { n: sig.daysPlayed })];
  if (lc.stepStates.length > 0) {
    parts.push(fill(t.rosterSteps, { n: sig.stepsMovedUp }));
    if (sig.longHelp > 0) parts.push(fill(t.rosterLongHelp, { n: sig.longHelp }));
  }
  if (sig.suggestionsSeen > 0) parts.push(fill(t.rosterSeen, { n: sig.suggestionsSeen }));
  return (
    <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexWrap: "wrap", gap: "2px 14px" }}>
      {parts.map((x) => (
        <li key={x} style={{ fontSize: 13.5, color: "var(--ink-soft)" }}>
          {x}
        </li>
      ))}
    </ul>
  );
}

// ---- Single-child focused home -----------------------------------------------

function SingleChildHome({
  lc,
  dict,
}: {
  lc: LinkedChild;
  dict: ReturnType<typeof getDictionary>;
}) {
  const navigate = useNavigate();
  const t = dict.service.home;
  const { locale } = useComfort();
  const { child, sessions } = lc;
  const lastSession = sessions[0];
  const firstName = child.displayLabel.split(" ")[0];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* Child card header */}
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <span
          aria-hidden="true"
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            background: "var(--brand-tint)",
            display: "grid",
            placeItems: "center",
            flex: "none",
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: 22,
            color: "var(--brand)",
          }}
        >
          {child.displayLabel.charAt(0)}
        </span>
        <div>
          <h1
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 700,
              fontSize: 26,
              color: "var(--ink-deep)",
              margin: 0,
            }}
          >
            {t.greeting}, {firstName}
          </h1>
          {lastSession && (
            <p
              style={{
                color: "var(--ink-faint)",
                fontSize: 13.5,
                margin: "4px 0 0",
              }}
            >
              {fill(t.lastPlayed, { date: formatDate(locale, lastSession.at) })}
            </p>
          )}
        </div>
      </div>

      {/* This week */}
      <section>
        <SectionHeading>{t.thisWeek}</SectionHeading>
        <div
          style={{
            background: "var(--surface)",
            border: "1px solid var(--line)",
            borderRadius: "var(--r-lg)",
            padding: "14px 20px",
          }}
        >
          <SignalLines lc={lc} dict={dict} />
        </div>
      </section>

      {/* Actions */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <button
          onClick={() => navigate(`/children/${child.id}`)}
          style={{
            minHeight: 48,
            padding: "0 22px",
            border: "1.5px solid var(--brand)",
            background: "transparent",
            color: "var(--brand)",
            fontFamily: "var(--font-body)",
            fontWeight: 600,
            fontSize: 15,
            borderRadius: "var(--r-pill)",
            cursor: "pointer",
          }}
        >
          {t.seeProgress}
        </button>
      </div>

      {/* Engagement guardrail + sync framing */}
      <p style={{ fontSize: 13, color: "var(--ink-faint)", margin: 0 }}>
        {t.engagementOnly}
      </p>
      <SyncNote at={lastSession?.at} />
    </div>
  );
}

// ---- Many-children roster home -----------------------------------------------

function ManyChildrenHome({
  linked,
  dict,
}: {
  linked: LinkedChild[];
  dict: ReturnType<typeof getDictionary>;
}) {
  const navigate = useNavigate();
  const t = dict.service.home;
  const tc = dict.service.children;
  const { sessions, activeChildren, summariesShared } = thisWeekStats(linked);
  const lastSyncedAt = linked
    .flatMap((lc) => lc.sessions)
    .map((s) => s.at)
    .sort()
    .at(-1);
  const rosterChildren = linked.filter(
    (lc) =>
      lc.linkState === "active" &&
      (lc.link.relation === "owner" || lc.link.scope.includes("progress")),
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
      <h1
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: 28,
          color: "var(--ink-deep)",
          margin: 0,
        }}
      >
        {t.greeting}
      </h1>

      {/* This-week engagement stats */}
      <section>
        <SectionHeading>{t.thisWeek}</SectionHeading>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 12,
          }}
        >
          <StatTile value={String(sessions)} label={t.sessionsPlayed} />
          <StatTile value={String(activeChildren)} label={t.childrenActive} />
          <StatTile value={String(summariesShared)} label={t.summariesShared} />
        </div>
      </section>

      {/* This week, child by child (design §7c) — neutral counts, no judgement */}
      {rosterChildren.length > 0 && (
        <section>
          <SectionHeading>{t.rosterTitle}</SectionHeading>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {rosterChildren.map((lc) => (
              <button
                key={lc.child.id}
                onClick={() => navigate(`/children/${lc.child.id}`)}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 4,
                  padding: "14px 18px",
                  background: "var(--surface)",
                  border: "1px solid var(--line)",
                  borderRadius: "var(--r-lg)",
                  cursor: "pointer",
                  textAlign: "left",
                  minHeight: 56,
                  fontFamily: "var(--font-body)",
                }}
              >
                <b style={{ fontSize: 15.5, color: "var(--ink-deep)" }}>{lc.child.displayLabel}</b>
                <SignalLines lc={lc} dict={dict} />
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Children roster */}
      <section>
        <SectionHeading>{tc.title}</SectionHeading>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {linked.map((lc) => (
            <ChildRow
              key={lc.child.id}
              lc={lc}
              dict={dict}
              onClick={
                lc.linkState === "active"
                  ? () => navigate(`/children/${lc.child.id}`)
                  : undefined
              }
            />
          ))}
        </div>
      </section>

      <p style={{ fontSize: 13, color: "var(--ink-faint)", margin: 0 }}>
        {t.engagementOnly}
      </p>
      <SyncNote at={lastSyncedAt} />
    </div>
  );
}

// ---- Empty state --------------------------------------------------------------

function EmptyHome({ dict }: { dict: ReturnType<typeof getDictionary> }) {
  const navigate = useNavigate();
  const tc = dict.service.children;
  const tl = dict.service.link;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 300,
        textAlign: "center",
        gap: 16,
      }}
    >
      <h1
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: 24,
          color: "var(--ink-deep)",
          margin: 0,
        }}
      >
        {tc.emptyTitle}
      </h1>
      <p
        style={{
          color: "var(--ink-soft)",
          fontSize: 15.5,
          lineHeight: 1.6,
          maxWidth: 400,
          margin: 0,
        }}
      >
        {tc.emptyBody}
      </p>
      <button
        onClick={() => navigate("/link")}
        style={{
          marginTop: 8,
          minHeight: 48,
          padding: "0 24px",
          border: 0,
          borderRadius: "var(--r-pill)",
          background: "var(--brand)",
          color: "#fff",
          fontFamily: "var(--font-body)",
          fontWeight: 600,
          fontSize: 15,
          cursor: "pointer",
        }}
      >
        {tl.title}
      </button>
    </div>
  );
}

// ---- Adaptive Home export ----------------------------------------------------

export function Home() {
  const { locale } = useComfort();
  const dict = getDictionary(locale);
  const outlet = useOutletContext<{ userId?: string } | null>();
  const { linked, loading, error, refresh } = useLinkedChildren(outlet?.userId);

  // Order matters: "still loading" and "the fetch failed" must both win over
  // "you have no children", or a parent is told their children are gone every
  // time the network is slow or the session needs refreshing.
  if (loading) return <LoadingChildren />;
  if (error) return <ChildrenLoadError message={error} onRetry={refresh} />;

  if (linked.length === 0) {
    return <EmptyHome dict={dict} />;
  }

  if (linked.length === 1) {
    return <SingleChildHome lc={linked[0]} dict={dict} />;
  }

  return <ManyChildrenHome linked={linked} dict={dict} />;
}
